import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useHistory } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { fetchStatusBarData } from '../api';
import { getRelatedSiteUrl } from '../constants/relatedSites';
import { isNoticeMenuLabel } from '../constants/noticeMenu';
import { usePublicNotices } from '../hooks/usePublicNotices';
import { publicAsset } from '../utils/publicAsset';

// ===== Helper Function =====
const formatDate = (date: Date, format: string) => {
  const month = date.getMonth() + 1;
  const day = date.getDate();
  return format.replace('{month}', month.toString()).replace('{day}', day.toString());
};

// 달력 아이콘 컴포넌트
const CalendarIcon = () => (
  <svg width="18" height="18" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="2" y="3" width="12" height="10" rx="2" fill="#e3f2fd" stroke="#1e3a8a" strokeWidth="1.5"/>
    <rect x="4" y="5" width="2" height="2" rx="1" fill="#1e3a8a"/>
    <rect x="7" y="5" width="2" height="2" rx="1" fill="#1e3a8a"/>
    <rect x="10" y="5" width="2" height="2" rx="1" fill="#1e3a8a"/>
    <rect x="4" y="8" width="2" height="2" rx="1" fill="#1e3a8a"/>
    <rect x="7" y="8" width="2" height="2" rx="1" fill="#1e3a8a"/>
    <line x1="6" y1="1" x2="6" y2="3" stroke="#1e3a8a" strokeWidth="1.5"/>
    <line x1="10" y1="1" x2="10" y2="3" stroke="#1e3a8a" strokeWidth="1.5"/>
    <line x1="2" y1="6" x2="14" y2="6" stroke="#1e3a8a" strokeWidth="1.5"/>
  </svg>
);

interface SidebarProps {
  onItemClick: (item: string) => void;
  className?: string;
  isMobile?: boolean;
}

const Sidebar: React.FC<SidebarProps> = ({ onItemClick, className, isMobile = false }) => {
  const [expandedSection, setExpandedSection] = useState<number | null>(null);
  const { t } = useLanguage();
  const history = useHistory();
  const { hasNotices } = usePublicNotices();

  // 날씨 정보 상태
  const [date, setDate] = useState('');
  const [temperature, setTemperature] = useState<number | null>(null);
  const [weatherIcon, setWeatherIcon] = useState('');
  const [aqiValue, setAqiValue] = useState<number | null>(null);
  const [aqiIcon, setAqiIcon] = useState('');

  // 날씨 데이터 가져오기
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      const now = new Date();
      setDate(formatDate(now, t.statusBar.dateFormat));
      setTemperature(26);
      setWeatherIcon(`${publicAsset('/assets/img/sky/sky_01.webp')}`);
      setAqiValue(32);
      setAqiIcon(`${publicAsset('/assets/img/aqi/wt_normal.webp')}`);
    } else {
      fetchStatusBarData()
        .then(data => {
          if (!data) return;

          if (data.date) {
            const dateObj = new Date(data.date);
            if (!isNaN(dateObj.getTime())) {
              setDate(formatDate(dateObj, t.statusBar.dateFormat));
            } else {
              const now = new Date();
              setDate(formatDate(now, t.statusBar.dateFormat));
            }
          } else {
            setDate('');
          }
          setTemperature(data.temperature);

          const skyCode = data.sky_code || 1;
          setWeatherIcon(publicAsset(`/assets/img/sky/sky_0${skyCode}.webp`));

          setAqiValue(data.aqi_value || 0);

          const iconMap: { [key: number]: string } = {
            1: 'wt_good.webp',
            2: 'wt_normal.webp',
            3: 'wt_bad.webp',
            4: 'wt_verybad.webp'
          };

          const aqiGrade = data.aqi_grade || 2;
          setAqiIcon(publicAsset(`/assets/img/aqi/${iconMap[aqiGrade]}`));
        })
        .catch(() => {
          const now = new Date();
          setDate(formatDate(now, t.statusBar.dateFormat));
          setTemperature(null);
          setWeatherIcon(`${publicAsset('/assets/img/sky/sky_01.webp')}`);
          setAqiValue(0);
          setAqiIcon(`${publicAsset('/assets/img/aqi/wt_normal.webp')}`);
        });
    }
  }, [t]);

  const toggleSection = (index: number) => {
    setExpandedSection(prev => (prev === index ? null : index));
  };

  const goHome = () => {
    const path = history.location.pathname;
    const onHome =
      path === '/' || path === '/en' || path === '/ja' || path === '/cn';
    if (onHome) {
      window.location.reload();
      return;
    }
    history.push('/');
  };

  // Laptop 버전에서는 '챗봇 도움말' 메뉴를 제외 / 공지 없으면 공지 메뉴 숨김
  const filteredMenuItems = t.menuItems.filter((menu: any) => {
    if (isNoticeMenuLabel(menu.label) && !hasNotices) {
      return false;
    }
    if (!isMobile) {
      // Laptop 버전에서는 '챗봇 도움말' 관련 메뉴 제외
      const chatbotLabels = ['챗봇 도움말', 'Chatbot Help', 'チャットボットヘルプ', '聊天机器人帮助'];
      return !chatbotLabels.includes(menu.label);
    } else {
      // 모바일에서는 '여권신청 대기인원' 관련 메뉴 제외
      const passportLabels = ['여권신청 대기인원', 'Passport Application Queue', 'パスポート申請待機人員', '护照申请等待人员'];
      return !passportLabels.includes(menu.label);
    }
  });

  return (
    <SidebarContainer className={className}>
      <LogoSection>
        <LogoWrapper onClick={goHome} title="홈으로" role="button">
        </LogoWrapper>
        {!isMobile && (
          <AIChungnyeongTitle onClick={goHome} role="button">
            AI충녕
          </AIChungnyeongTitle>
        )}
      </LogoSection>

      {!isMobile && (
        <WeatherSection>
          {/* <WeatherTitle>
            <WeatherTitleIcon>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zM12 11.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" fill="#4a5568"/>
              </svg>
            </WeatherTitleIcon>
            <WeatherTitleText>세종시</WeatherTitleText>
          </WeatherTitle> */}

          <WeatherGrid>
            <WeatherCard>
              <WeatherCardIcon>
                <CalendarIcon />
              </WeatherCardIcon>
              <WeatherCardContent>
                <WeatherCardLabel>{t.statusBar.todayDate}</WeatherCardLabel>
                <WeatherCardValue>{date || '정보 없음'}</WeatherCardValue>
              </WeatherCardContent>
            </WeatherCard>

            <WeatherCard>
              <WeatherCardIcon>
                <WeatherIcon src={weatherIcon || `${publicAsset('/assets/img/sky/sky_01.webp')}`} alt="날씨" />
              </WeatherCardIcon>
              <WeatherCardContent>
                <WeatherCardLabel>{t.statusBar.currentTemp}</WeatherCardLabel>
                <WeatherCardValue>{temperature ?? '-'}°C</WeatherCardValue>
              </WeatherCardContent>
            </WeatherCard>

            <WeatherCard>
              <WeatherCardIcon>
                <AqiIcon src={aqiIcon || `${publicAsset('/assets/img/aqi/wt_normal.webp')}`} alt="미세먼지" />
              </WeatherCardIcon>
              <WeatherCardContent>
                <WeatherCardLabel>{t.statusBar.airQuality}</WeatherCardLabel>
                <WeatherCardValue>{aqiValue ?? 0} µg/m³</WeatherCardValue>
              </WeatherCardContent>
            </WeatherCard>
          </WeatherGrid>
        </WeatherSection>
      )}

      <MenuSection>
        {filteredMenuItems.map((menu: any, index: number) => {
          const isNotice = isNoticeMenuLabel(menu.label);
          return (
          <CategorySection key={index}>
            <CategoryHeader
              onClick={() => {
                if (isNotice) {
                  history.push('/notices');
                  return;
                }
                toggleSection(index);
              }}
              $isExpanded={expandedSection === index}
            >
              <CategoryTitle>{menu.label}</CategoryTitle>
            </CategoryHeader>
            
            {!isNotice && (
            <CategoryContent $isExpanded={expandedSection === index}>
              {menu.items.map((item: string, itemIndex: number) => {
                // 각 언어별 "담당자 전화번호" 항목 확인
                const contactPhoneItems = [
                  '담당자 전화번호',      // 한국어
                  'Contact Phone Number', // 영어
                  '担当者電話番号',      // 일본어
                  '负责人电话号码'       // 중국어
                ];
                const isLinkItem = contactPhoneItems.includes(item);
                const linkUrl = 'https://www.sejong.go.kr/prog/deptPerson/kor/sub01_0103/deptList.do';

                return isLinkItem ? (
                  <ExternalLink
                    key={itemIndex}
                    href={linkUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {item}
                  </ExternalLink>
                ) : (
                  <MenuItem
                    key={itemIndex}
                    onClick={() => {
                      const relatedUrl = getRelatedSiteUrl(item);
                      if (relatedUrl) {
                        window.open(relatedUrl, '_blank', 'noopener,noreferrer');
                      } else {
                        onItemClick(item);
                      }
                    }}
                  >
                    {item}
                  </MenuItem>
                );
              })}
            </CategoryContent>
            )}
          </CategorySection>
          );
        })}
      </MenuSection>

      <FooterSection>
        <ContactInfo>
          <strong>{t.sidebar.mainPhone}</strong><br />
          {t.sidebar.weekdayHours}<br />
          {t.sidebar.weekendHours}<br />
          {t.sidebar.afterHours}
        </ContactInfo>
      </FooterSection>
    </SidebarContainer>
  );
};

export default Sidebar;

const SidebarContainer = styled.div`
  width: 280px;
  height: 100vh;
  background: ${props => props.theme.bgColor};
  border-right: 1px solid ${props => props.theme.borderGrayColor};
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  color: ${props => props.theme.textDefaultColor};

  /* 스크롤바 스타일링 */
  scrollbar-width: thin;
  scrollbar-color: rgba(0, 0, 0, 0.2) transparent;

  &::-webkit-scrollbar {
    width: 5px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background-color: rgba(0, 0, 0, 0.2);
    border-radius: 10px;
    border: transparent;
  }

  &:hover::-webkit-scrollbar-thumb {
    background-color: rgba(0, 0, 0, 0.3);
  }
`;

const LogoSection = styled.div`
  padding: 2.5rem 1.5rem 1.5rem 1.5rem;
  text-align: center;
  border-bottom: 1px solid ${props => props.theme.borderGrayColor};
  background: ${props => props.theme.bgColor};
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const WeatherSection = styled.div`
  padding: 1.5rem;
  border-bottom: 1px solid ${props => props.theme.borderGrayColor};
  background: ${props => props.theme.bgColor};
  display: flex;
  flex-direction: column;
  gap: 1.2rem;
`;


const WeatherGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.8rem;
`;

const WeatherCard = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem;
  background: ${props => props.theme.questionBoxBg};
  border-radius: 8px;
  border: 1px solid ${props => props.theme.borderGrayColor};
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    background: ${props => props.theme.bgColor};
  }
`;

const WeatherCardIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  flex-shrink: 0;
`;

const WeatherCardContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  flex: 1;
`;

const WeatherCardLabel = styled.span`
  font-size: 0.875rem;
  font-weight: 500;
  color: ${props => props.theme.textThinColor};
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const WeatherCardValue = styled.span`
  font-size: 1.5rem;
  font-weight: 700;
  color: ${props => props.theme.textDefaultColor};
`;


const LogoWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 130px;
  height: 130px;
  background:
    url(${publicAsset('/assets/img/character_1.webp')}) center top / 95px auto no-repeat;
  margin-bottom: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-3px) scale(1.08);
  }
`;


const AIChungnyeongTitle = styled.h2`
  font-size: 2.3rem;
  font-weight: 800;
  color: ${props => props.theme.textDefaultColor === '#FFF' ? '#FFF' : 'rgb(19, 43, 87)'};
  margin: 0;
  text-align: center;
  cursor: pointer;
  transition: color 0.2s ease;

  &:hover {
    color: ${props => props.theme.textThinColor};
  }
`;


const MenuSection = styled.div`
  flex: 1;
  padding: 1rem 0;
`;

const CategorySection = styled.div`
  border-bottom: 1px solid ${props => props.theme.borderGrayColor};
`;

const CategoryHeader = styled.div<{ $isExpanded: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.2rem 1.5rem;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  background: ${props => props.$isExpanded ? props.theme.questionBoxBg : 'transparent'};
  border-radius: 12px;
  margin: 0.25rem 0.5rem;

  &:hover {
    background: ${props => props.theme.questionBoxBg};
    transform: scale(1.02);
    box-shadow: 0 3px 12px rgba(109, 40, 217, 0.25);
  }
`;

const CategoryTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 600;
  color: ${props => props.theme.textDefaultColor};
  margin: 0;
`;


const CategoryContent = styled.div<{ $isExpanded: boolean }>`
  max-height: ${props => props.$isExpanded ? '800px' : '0'};
  overflow-y: auto;
  overflow-x: hidden;
  transition: max-height 0.3s ease;
  background: ${props => props.theme.questionBoxBg};

  /* 커스텀 스크롤바 */
  &::-webkit-scrollbar {
    width: 5px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background-color: rgba(109, 40, 217, 0.3);
    border-radius: 10px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background-color: rgba(109, 40, 217, 0.5);
  }
`;

const MenuItem = styled.div`
  padding: 0.8rem 2rem;
  cursor: pointer;
  color: ${props => props.theme.textDefaultColor};
  font-size: 1.4rem;
  transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  border-left: 3px solid transparent;
  border-radius: 0 8px 8px 0;

  &:hover {
    background: ${props => props.theme.questionBoxBg};
    color: ${props => props.theme.logoColor};
    border-left-color: ${props => props.theme.logoColor};
    transform: translateX(4px);
    box-shadow: 0 2px 8px rgba(109, 40, 217, 0.2);
  }
`;

const ExternalLink = styled.a`
  display: block;
  padding: 0.8rem 2rem;
  color: ${props => props.theme.textDefaultColor};
  font-size: 1.4rem;
  text-decoration: none;
  transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  border-left: 3px solid transparent;
  border-radius: 0 8px 8px 0;

  &:hover {
    background: ${props => props.theme.questionBoxBg};
    color: ${props => props.theme.logoColor};
    border-left-color: ${props => props.theme.logoColor};
    transform: translateX(4px);
    box-shadow: 0 2px 8px rgba(109, 40, 217, 0.2);
  }
`;

const FooterSection = styled.div`
  padding: 1.5rem;
  border-top: 1px solid ${props => props.theme.borderGrayColor};
  background: ${props => props.theme.bgColor};
`;

const ContactInfo = styled.div`
  font-size: 1.1rem;
  color: ${props => props.theme.textThinColor};
  line-height: 1.5;
  text-align: center;

  strong {
    color: ${props => props.theme.logoColor};
    font-size: 1.2rem;
  }
`;

const WeatherIcon = styled.img`
  width: 28px;
  height: 28px;
  border-radius: 4px;
`;

const AqiIcon = styled.img`
  width: 28px;
  height: 28px;
  border-radius: 4px;
`;
